import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

import HelpButton from './Help';
import { lineIntersection, isValidEdge, findCycles } from '../utils/geometry';

const SVG_SIZE = 300;
const WHEEL_RADIUS = 100;
const CENTER = { x: SVG_SIZE / 2, y: SVG_SIZE / 2 };
const LETTER_RADIUS = WHEEL_RADIUS * 1.1;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function getLetterPositions() {
  const letterPositions = {};

  ALPHABET.split("").forEach((letter, index) => {
    const angle = (index / ALPHABET.length) * 2 * Math.PI - Math.PI / 2;
    letterPositions[letter] = {
      x: CENTER.x + WHEEL_RADIUS * Math.cos(angle),
      y: CENTER.y + WHEEL_RADIUS * Math.sin(angle),
      labelX: CENTER.x + LETTER_RADIUS * Math.cos(angle),
      labelY: CENTER.y + LETTER_RADIUS * Math.sin(angle),
    };
  });

  return letterPositions;
}

function getLetterPoints(letters, letterPositions) {
  return letters.split("").map((letter) => ({
    letter,
    x: letterPositions[letter]?.x,
    y: letterPositions[letter]?.y,
    labelX: letterPositions[letter]?.labelX,
    labelY: letterPositions[letter]?.labelY,
  }));
}

function getPathLines(points) {
  const lines = [];
  for (let i = 0; i < points.length - 1; i++) {
    lines.push([points[i], points[i + 1]]);
  }

  return lines;
}

function drawPath(container, points, color, thickness, drawLines = true) {
  points.forEach((point) => {
    if (point.x != null && point.y != null) {
      container
        .append("circle")
        .attr("cx", point.x)
        .attr("cy", point.y)
        .attr("r", 5)
        .attr("fill", color);
    }
  });

  const lines = getPathLines(points);
  if (drawLines) {
    lines.forEach(([start, end]) => {
      if (start.x != null && start.y != null && end.x != null && end.y != null) {
        container
          .append("line")
          .attr("x1", start.x)
          .attr("y1", start.y)
          .attr("x2", end.x)
          .attr("y2", end.y)
          .attr("stroke", color)
          .attr("stroke-width", thickness);
      }
    });
  }

  return lines;
}

const WordWheel = ({ word }) => {
  const svgRef = useRef(null);
  const [error, setError] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const normalizedWord = word.toUpperCase();

  useEffect(() => {
    if (userInput === normalizedWord) {
      setShowAlert(true);
    }
  }, [userInput, normalizedWord]);

  useEffect(() => {
    if (showAlert) {
      alert("Congratulations! You solved the puzzle.");
    }
  }, [showAlert]);

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }

    const renderPuzzle = async () => {
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      setError(null);

      svg
        .attr("width", SVG_SIZE)
        .attr("height", SVG_SIZE)
        .attr("viewBox", `0 0 ${SVG_SIZE} ${SVG_SIZE}`);

      const letterPositions = getLetterPositions();

      svg
        .append("circle")
        .attr("cx", CENTER.x)
        .attr("cy", CENTER.y)
        .attr("r", WHEEL_RADIUS)
        .attr("fill", "none")
        .attr("stroke", "#D8D0C5")
        .attr("stroke-width", 1.5);

      Object.entries(letterPositions).forEach(([letter, pos]) => {
        svg
          .append("text")
          .attr("x", pos.labelX)
          .attr("y", pos.labelY)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "central")
          .attr("font-size", "12")
          .attr("font-weight", "600")
          .attr("fill", "#7A7168")
          .text(letter);
      });

      // Identify points corresponding to daily word letters
      const wordPoints = getLetterPoints(normalizedWord, letterPositions);
      const lines = getPathLines(wordPoints);

      // Identify intersection points
      const intersections = [];
      for (let i = 0; i < lines.length; i++) {
        for (let j = i + 1; j < lines.length; j++) {
          const int = lineIntersection(
            lines[i][0],
            lines[i][1],
            lines[j][0],
            lines[j][1],
          );
          if (int) intersections.push(int);
        }
      }

      // Identify all distinct vertices
      const vertexSet = new Set();
      const addVertex = (point) => {
        const key = `${point.x.toFixed(6)},${point.y.toFixed(6)}`;
        if (!vertexSet.has(key)) {
          vertexSet.add(key);
          return true;
        }
        return false;
      };

      const allVertices = [];
      wordPoints.forEach((point) => {
        if (addVertex(point)) allVertices.push(point);
      });
      intersections.forEach((point) => {
        if (addVertex(point)) allVertices.push(point);
      });

      // Identify all valid edges
      const edges = [];
      for (let i = 0; i < allVertices.length; i++) {
        for (let j = i + 1; j < allVertices.length; j++) {
          if (isValidEdge(allVertices[i], allVertices[j], lines, allVertices)) {
            edges.push([i, j]);
          }
        }
      }

      // Find cycles and fill shapes
      const regionColor = "black";
      try {
        const cycles = findCycles(allVertices, edges);
        cycles.forEach((cycle) => {
          const cyclePoints = cycle.map((i) => allVertices[i]);
          const d = d3.line()(cyclePoints.map((p) => [p.x, p.y])) + "Z";
          svg
            .append("path")
            .attr("d", d)
            .attr("fill", regionColor)
            .attr("stroke", "none");
        });
      } catch (error) {
        console.error("Error in findCycles:", error.message);
        setError("Puzzle generation failed. Please try again.");
        return;
      }

      // Draw the daily word's letter dots and connecting lines on top of the filled regions
      drawPath(svg, wordPoints, "black", 1);

      svg.append("g").attr("class", "user-input-layer");
    };

    renderPuzzle().catch((err) => {
      console.error("Unhandled rendering error:", err.message);
      setError("An unexpected error occurred. Please try again.");
    });
  }, [normalizedWord]);

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }

    const svg = d3.select(svgRef.current);
    const overlay = svg.select(".user-input-layer");
    if (overlay.empty()) {
      return;
    }

    overlay.selectAll("*").remove();
    const letterPositions = getLetterPositions();
    const userInputPoints = getLetterPoints(userInput.toUpperCase(), letterPositions);
    drawPath(overlay, userInputPoints, "red", 3);
  }, [userInput]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream text-ink font-sans px-6">
        <p className="text-ink-muted">{error}</p>
      </div>
    );
  }

  const handleChange = (e) => {
    const input = e.target.value.toUpperCase();
    const validInput = input
      .split("")
      .filter((letter) => word.toUpperCase().includes(letter))
      .join("");
    setUserInput(validInput);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-cream text-ink font-sans px-6 py-10 box-border">
      <HelpButton />
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-card px-8 py-8 flex flex-col items-center">
        <p className="text-xs font-semibold tracking-wide text-ink-muted uppercase mb-4">
          Today's answer is a {normalizedWord.length} letter word
        </p>
        <svg ref={svgRef}></svg>
        <input
          type="text"
          value={userInput}
          onChange={handleChange}
          className="mt-6 w-full max-w-[240px] text-center font-semibold tracking-[0.2em] uppercase bg-cream rounded-full px-5 py-3 outline-none border-2 border-transparent focus:border-coral transition-colors placeholder:normal-case placeholder:tracking-normal placeholder:font-normal placeholder:text-ink-muted"
          placeholder="Type letters here..."
        />
      </div>
    </div>
  );
};

export default WordWheel;
